import { Card } from 'shared/ui/card/card';
import cls from './suggestion.module.scss';
import { useCachedSelectedProject, useTranslation } from 'hooks';
import { Chip } from 'shared/ui/chip/chip';
import Button from 'shared/ui/button/button';
import { useState } from 'react';
import Drawer from 'shared/ui/drawer/drawer';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';
import { ICONS } from 'shared/constants/images';
import { Icon } from 'shared/ui/icon/icon';
import { AnalysisItem } from './analysisItem';
import { useLocation } from 'react-router-dom';
import { RuleDescription } from 'widgets/score-disclosure/components/rules/rule-description';
import { TechRulesAssesment } from 'components/tech-rules-assessment/tech-rules-assessment';
import { linkifyText } from 'shared/helpers/linkifyText';
import { ScoreSquare } from '../score-square/score-square';
import { getPagePrefix, track } from 'utils/mixpanel';
import { getRuleTrackName } from 'widgets/score-disclosure/helpers';
import { RuleExample, ScoreEnum } from 'store/api/apiTypes';
import { TrialBanner } from 'shared/ui/trial-banner/trial-banner';

interface SuggestionProps {
  title: string;
  score: ScoreEnum;
  text: string;
  analysis: RuleExample[];
  color?: 'gray' | 'white';
  showScore?: boolean;
  showAnalysis?: boolean;
  needFix?: boolean;
  chipContent?: React.ReactNode;
  titleCN?: string;
  reference?: string;
  showChip?: boolean;
  btnText?: string;
  rulesReverse?: boolean;
  restricted?: boolean;
  productNumber?: number;
  typeName?: string;
}

export const Suggestion: React.FC<SuggestionProps> = ({
  text,
  score,
  title,
  analysis,
  chipContent,
  titleCN,
  btnText,
  productNumber,
  typeName,
  color = 'gray',
  showScore = true,
  showAnalysis = true,
  needFix = false,
  showChip = true,
  reference = 'academicReference',
  rulesReverse = false,
  restricted = false,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { isTrial, isTrialExpired, testingB } = useCachedSelectedProject();

  const scoreTitle = (
    {
      1: t('bad'),
      2: t('average'),
      3: t('good'),
    } as const
  )[score];

  const scoreColor = (
    {
      1: 'red',
      2: 'orange',
      3: 'green',
    } as const
  )[score];
  const ruleName = getRuleTrackName(title);
  const pagePrefix = getPagePrefix(location.pathname);
  const handleOpen = () => {
    track(`AIS:${pagePrefix}_CRD_ANALYSIS-OPENED`, {
      type: typeName ?? ruleName,
      is_trial: isTrial,
      ...(productNumber && { product: productNumber }),
      ...(typeName && { sub_type: ruleName }),
    });
    setIsOpen(true);
  };

  const handleClose = () => {
    track(`AIS:${pagePrefix}_CRD_ANALYSIS-CLOSED`, {
      type: typeName ?? ruleName,
      is_trial: isTrial,
      ...(productNumber && { product: productNumber }),
      ...(typeName && { sub_type: ruleName }),
    });
    setIsOpen(false);
  };

  const trackDisclosureClick = (cardType: string, open: boolean) => {
    track(
      !open
        ? `AIS:${pagePrefix}_CRD_ANALYSIS_SUBSECTION-EXPANDED`
        : `AIS:${pagePrefix}_CRD_ANALYSIS_SUBSECTION-COLAPSED`,
      {
        is_trial: isTrial,
        type: typeName ?? ruleName,
        card_type: cardType,
        ...(productNumber && { product: productNumber }),
        ...(typeName && { sub_type: ruleName }),
      }
    );
  };

  return (
    <>
      <Card className={`${cls.suggestion} ${cls[color]}`}>
        <div className={cls.header}>
          <h4 className={titleCN ?? ''}>{t(title)}</h4>
          {isTrialExpired ? (
            <ScoreSquare score={0} isLocked />
          ) : (
            <Chip color={showChip ? scoreColor : 'gray'}>
              {showChip ? (
                showScore ? (
                  <span>
                    {t('websiteScore')} <b>• {scoreTitle}</b>
                  </span>
                ) : (
                  <span>{score === 3 ? t('Pass') : t('NeedFix')}</span>
                )
              ) : (
                <span>{t('analysisBeAvailableSoon')}</span>
              )}
            </Chip>
          )}
        </div>
        {!isTrialExpired && <p>{linkifyText(text)}</p>}
        <div className={cls.footer}>
          {showChip && !isTrialExpired && !restricted && (
            <Chip color='gray'>
              {chipContent ? (
                <>{chipContent}</>
              ) : (
                <>
                  {analysis.length} {t('casesFromWebsite')}
                </>
              )}
            </Chip>
          )}
          <Button
            theme='tonal'
            tone='default'
            shape={`${isTrialExpired || restricted ? 'square' : 'round'}`}
            onClick={handleOpen}
            disabled={isTrialExpired || restricted}
            className={`${cls.openButton}`}
          >
            {isTrialExpired || restricted ? (
              <span>
                <Icon pathToIcon={ICONS.lockGray} size={16} />
                {t('availableInFullAnalysis')}
              </span>
            ) : (
              (btnText ?? t('openAnalysis'))
            )}
          </Button>
        </div>
      </Card>
      <Drawer title={t(title)} isOpen={isOpen} onClose={handleClose}>
        <div
          className={`${cls.drawerContent} ${rulesReverse ? cls.reverse : ''}`}
        >
          <Disclosure defaultOpen>
            {({ open }) => (
              <Card className={cls.discCard}>
                <DisclosureButton
                  className={cls.disclosureButton}
                  onClick={() => trackDisclosureClick('Rule Assessment', open)}
                >
                  <h4>{t('ruleAssessment')}</h4>
                  <div className={cls.right}>
                    <Chip color={showChip ? scoreColor : 'gray'}>
                      {showChip ? (
                        <span>{scoreTitle}</span>
                      ) : (
                        <span>{t('analysisBeAvailableSoon')}</span>
                      )}
                    </Chip>
                    <Icon
                      pathToIcon={ICONS.arrowDown}
                      size={20}
                      className={open ? cls.open : ''}
                    />
                  </div>
                </DisclosureButton>
                <DisclosurePanel className={cls.panel}>
                  {linkifyText(text)}
                  {needFix && (
                    <Card className={cls.howToFix}>
                      <h4>{t('howToFix')}</h4>
                      <TechRulesAssesment
                        isTrial={isTrial && !testingB}
                        ruleKey={title}
                      />
                      {isTrial && !testingB && (
                        <TrialBanner
                          variant='subtleContrast'
                          title={t('trialBanner')}
                          description={t('trialBannerDescription')}
                          onActionClick={() =>
                            track(
                              `AIS:${pagePrefix}_CRD_DETAILS-UPGRADE_BTN-CLICKED`,
                              {
                                type: ruleName,
                                is_trial: isTrial,
                                ...(productNumber && {
                                  product: productNumber,
                                }),
                              }
                            )
                          }
                        />
                      )}
                    </Card>
                  )}
                </DisclosurePanel>
              </Card>
            )}
          </Disclosure>
          <Disclosure>
            {({ open }) => (
              <Card className={cls.discCard}>
                <DisclosureButton
                  className={cls.disclosureButton}
                  onClick={() => trackDisclosureClick('Rule Description', open)}
                >
                  <h4>{t('ruleDescription')}</h4>
                  <div className={cls.right}>
                    <Chip color='blue' iconPath={ICONS.book}>
                      <span>{t(reference)}</span>
                    </Chip>
                    <Icon
                      pathToIcon={ICONS.arrowDown}
                      size={20}
                      className={open ? cls.open : ''}
                    />
                  </div>
                </DisclosureButton>
                <DisclosurePanel className={cls.panel}>
                  <RuleDescription
                    ruleTitle={title}
                    productNumber={productNumber}
                  />
                </DisclosurePanel>
              </Card>
            )}
          </Disclosure>
          {showAnalysis && (
            <Disclosure defaultOpen>
              {({ open }) => (
                <Card className={cls.discCard}>
                  <DisclosureButton
                    className={cls.disclosureButton}
                    onClick={() =>
                      trackDisclosureClick('Website Analysis', open)
                    }
                  >
                    <h4>{t('websiteAnalysis')}</h4>
                    <div className={cls.right}>
                      <Chip color='blue' iconPath={ICONS.assignment}>
                        <span>
                          {analysis.length} {t('casesFromWebsite')}
                        </span>
                      </Chip>
                      <Icon
                        pathToIcon={ICONS.arrowDown}
                        size={20}
                        className={open ? cls.open : ''}
                      />
                    </div>
                  </DisclosureButton>
                  <DisclosurePanel className={cls.panel}>
                    <p>
                      {t('websiteAnalysisDescription', undefined, undefined, {
                        count: analysis.length,
                      })}
                    </p>
                    {isTrial && !testingB ? (
                      <div className={cls.cardsContainer}>
                        {analysis[0] && (
                          <>
                            <AnalysisItem
                              item={analysis[0]}
                              index={0}
                              isTrial
                            />
                            <TrialBanner
                              title={t('trialBanner')}
                              description={t('trialBannerDescription')}
                            />
                          </>
                        )}
                      </div>
                    ) : (
                      <div className={cls.cardsContainer}>
                        {analysis.map((item, index) => (
                          <AnalysisItem
                            key={index}
                            item={item}
                            index={index}
                            productNumber={productNumber}
                            ruleName={ruleName}
                          />
                        ))}
                      </div>
                    )}
                  </DisclosurePanel>
                </Card>
              )}
            </Disclosure>
          )}
        </div>
      </Drawer>
    </>
  );
};
